import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Input } from '@/src/renderer/components/ui/Input'
import { Button } from '@/src/renderer/components/ui/Button'
import { FacePlusIcon } from '@/src/renderer/components/icons/FacePlusIcon'
import { ProfilePictureIcon } from '@/src/renderer/components/icons/ProfilePictureIcon'
import { AvatarPickerPopover } from '@/src/renderer/components/common/AvatarPickerPopover'
import { useUserDisplayProfile } from '@/src/renderer/hooks/useUserDisplayProfile'
import { saveProfileAvatar, saveProfileName } from '@/src/renderer/lib/profile'
import { SettingsSection } from './primitives/SettingsSection'
import { SettingsRow } from './primitives/SettingsRow'

const NAME_MAX_LENGTH = 80

/**
 * Profile section under "App & Bedienung > Erscheinungsbild". Now follows
 * the shared SettingsSection/Row pattern used by the other sections, so
 * the visual rhythm of the page stays consistent.
 *
 * Autosaves per field — avatar on pick, name on blur. Empty name on blur
 * reverts to the last persisted value. Cloud vs. offline branching lives
 * inside {@link saveProfileName} / {@link saveProfileAvatar}.
 *
 * Reactivity after save flows through the `user-profile-updated` event:
 * {@link useUserDisplayProfile} re-fetches and the sidebar avatar reflects
 * the change.
 */
export function AccountProfileSection() {
  const { t } = useTranslation()
  // No useAuth() here — the standalone Electron preferences window has its
  // own React root without an AuthProvider. The hook resolves the user via
  // supabase.auth.getUser() internally and exposes email alongside the rest.
  // displaySize hint 128 (= 2× the visual 64px slot once zoomed) so the
  // hook resolves the 256px CDN variant.
  const { avatarUrl, fullName, avatarPresetId, email, isLoading } = useUserDisplayProfile(undefined, 128)

  const [name, setName] = useState('')
  const initialNameRef = useRef('')
  const [isSavingName, setIsSavingName] = useState(false)
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  useEffect(() => {
    if (fullName !== null) {
      setName(fullName)
      initialNameRef.current = fullName
    }
  }, [fullName])

  const handleNameBlur = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setName(initialNameRef.current)
      return
    }
    if (trimmed === initialNameRef.current) return

    setIsSavingName(true)
    try {
      await saveProfileName(trimmed)
      initialNameRef.current = trimmed
      setName(trimmed)
    } catch (err) {
      console.error('[AccountProfileSection] Failed to save name:', err)
      toast.error(t('profile.saveFailed'))
    } finally {
      setIsSavingName(false)
    }
  }, [name, t])

  // Toggle, not open: the picker's outside-click handler ignores clicks on
  // the anchor (i.e. this button), so a second click on the trigger must
  // close it explicitly — otherwise it stays open until the user picks an
  // avatar or clicks somewhere unrelated.
  const handleTogglePicker = useCallback(() => setIsPickerOpen((v) => !v), [])

  const handleSelectAvatar = useCallback(async (avatarId: string) => {
    setIsPickerOpen(false)
    try {
      await saveProfileAvatar(avatarId)
    } catch (err) {
      console.error('[AccountProfileSection] Failed to save avatar:', err)
      toast.error(t('profile.saveFailed'))
    }
  }, [t])

  const handleClosePicker = useCallback(() => setIsPickerOpen(false), [])

  return (
    <SettingsSection title={t('settings.section.profile')}>
      <SettingsRow
        label={t('profile.picture')}
        description={t('profile.pictureDescription')}
        leading={
          // 80×80 Squircle avatar preview, anchored far-left of the row.
          // `data-testid` preserved so the AccountProfileSection.test.tsx
          // suite still locates the slot.
          <div data-testid="account-profile-avatar">
            {isLoading ? (
              <div className="size-20 shrink-0" aria-hidden="true" />
            ) : (
              // `hoverZoom={false}` keeps the 80px slot static — a 2× zoom
              // here would clip past the SideNav border on the left.
              <ProfilePictureIcon size={80} selectedAvatarUrl={avatarUrl} hoverZoom={false} />
            )}
          </div>
        }
        control={
          // Relative wrapper anchors the picker popover directly under
          // the trigger button. `align="right"` opens it to the LEFT so
          // the 374px panel stays inside the settings content column.
          <div className="relative">
            <Button
              variant="outline"
              size="small"
              icon={<FacePlusIcon />}
              onClick={handleTogglePicker}
            >
              {t('profile.changeAvatar')}
            </Button>
            <AvatarPickerPopover
              isOpen={isPickerOpen}
              onClose={handleClosePicker}
              onSelectAvatar={handleSelectAvatar}
              selectedAvatarId={avatarPresetId}
              align="right"
            />
          </div>
        }
      />

      <SettingsRow
        label={t('profile.fullName')}
        description={t('profile.fullNameDescription')}
        htmlFor="profile-name"
        control={
          <Input
            id="profile-name"
            type="text"
            value={name}
            maxLength={NAME_MAX_LENGTH}
            autoComplete="name"
            disabled={isSavingName}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
            className="w-[240px]"
          />
        }
      />

      {email && (
        <SettingsRow
          label={t('profile.email')}
          description={t('profile.emailDescription')}
          htmlFor="profile-email"
          control={
            <Input
              id="profile-email"
              type="email"
              value={email}
              disabled
              readOnly
              className="w-[240px]"
            />
          }
        />
      )}
    </SettingsSection>
  )
}
